import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, HeaderCell, HeaderRow, SectionHeader, Table, TableBody, TableHead } from 'components';
import { DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import { selectCurrentEmployee } from 'store/App';
import { DisbursementMethod, IAcceptancePayout, ISiteRolePolicy, ITipAcceptancePaymentSetPayouts } from 'types';
import { formatJobTitle } from 'utils/jobs';
import { checkTipAcceptancePayoutForErrors } from 'utils/payout';
import { selectSiteAcceptanceBalance } from '../../../store/Site';
import { PayoutRow } from './PayoutRow';

interface IReviewModeProps {
  dataCy: string;
  isLoading: boolean;
  rolePolicy: ISiteRolePolicy;
  onActionClick(): void;
  paymentSet: ITipAcceptancePaymentSetPayouts;
  perTipMax: number;
  siteUsesHoldbacks: boolean;
}

const ReviewTableHead = styled(TableHead)`
  min-width: 100%;
`;

export const ReviewMode = ({ dataCy, isLoading, onActionClick, paymentSet, perTipMax, rolePolicy, siteUsesHoldbacks }: IReviewModeProps) => {
  const currentUserId = useSelector(selectCurrentEmployee())?.id;
  const acceptanceBalance = useSelector(selectSiteAcceptanceBalance());
  const payouts = paymentSet?.payouts;
  const isTableLoading = isLoading || !paymentSet || !payouts;

  const renderFillerCells = () => {
    const count = isTableLoading ? 3 : 1;
    const cells: JSX.Element[] = [];

    for (let i = 0; i < count; i++) {
      cells.push(<HeaderCell />);
    }

    return cells;
  };

  return (
    <>
      <SectionHeader dataCy={`${dataCy}-header`} isLoading={isTableLoading} title={`Review Payments (${paymentSet?.payouts.length})`}>
        <Button label="Edit Payments" size="small" isLoading={isTableLoading} disabled={acceptanceBalance <= 0} onClick={onActionClick} data-cy="tip-acceptance-edit-payments" />
      </SectionHeader>

      <Table scrollable sticky data-cy={`${dataCy}-table`}>
        <ReviewTableHead>
          <HeaderRow sticky data-cy={`${dataCy}-table-header-row`}>
            <HeaderCell>Name</HeaderCell>
            {siteUsesHoldbacks && <HeaderCell>Tip Amount</HeaderCell>}
            {siteUsesHoldbacks && <HeaderCell>Holdback</HeaderCell>}
            <HeaderCell>Paid</HeaderCell>
            {renderFillerCells()}
          </HeaderRow>
        </ReviewTableHead>

        <TableBody>
          {isTableLoading ? (
            <PayoutRow isLoading job="" tipAmount={0} holdbackAmount={0} disbursementMethod={DisbursementMethod.INSTANT} siteUsesHoldbacks />
          ) : (
            payouts.map((payout: IAcceptancePayout) => {
              const employeeSite = payout.employee.sites.find((site) => site.id === paymentSet.siteId);

              return (
                <PayoutRow
                  key={`payee-${payout.employee.id}`}
                  dataCy={`${dataCy}-payee`}
                  employee={payout.employee}
                  job={formatJobTitle(employeeSite?.job?.title || DEFAULT_ORG_ADMIN_JOB_TITLE)}
                  tipAmount={payout.amount}
                  holdbackAmount={siteUsesHoldbacks ? payout.holdbackAmount : 0}
                  disbursementMethod={employeeSite.disbursementMethod}
                  siteUsesHoldbacks={siteUsesHoldbacks}
                  isLoading={isLoading}
                  // only show individual payout errors if acceptance balance is greater than 0
                  hasError={acceptanceBalance > 0 && !!checkTipAcceptancePayoutForErrors(payout, employeeSite, payout.employee, rolePolicy, currentUserId, perTipMax)}
                />
              );
            })
          )}
        </TableBody>
      </Table>
    </>
  );
};
